import React, { useState } from 'react';
import { gtplSaathiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Search, Repeat, AlertTriangle, Tv, CheckCircle, XCircle } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { Screen, PageHead, Panel, Field, Notice } from '../components/gtpl/GtplKit';

// Swaps a subscriber's STB on GTPL Saathi's HardwareReplacementLCO.aspx. Search is fast
// (plain HTTP); the actual submit drives a real headless browser server-side — see
// gtplSaathiService.hardwareReplacement's comment for why (Telerik Entity dropdowns). NOT
// live-tested past search — the first few replacements here should be verified directly on
// gtplsaathi.com.
export default function GtplSaathiHardwareReplacementPage() {
  useDarkReady();
  const { hasModuleRight } = useAuth();
  const canReplace = hasModuleRight('gtplSaathi', 'hardwareReplacement');

  const [stb, setStb] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [searching, setSearching] = useState(false);
  const [options, setOptions] = useState(null);

  const [reason, setReason] = useState('');
  const [plan, setPlan] = useState('');
  const [status, setStatus] = useState('');
  const [newItemType, setNewItemType] = useState('');
  const [newSerial, setNewSerial] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!stb.trim() && !accountNumber.trim()) { toast.error('Enter an STB serial or account number'); return; }
    setSearching(true);
    setOptions(null);
    setResult(null);
    try {
      const { data } = await gtplSaathiAPI.hardwareReplacementSearch({ stb: stb.trim() || undefined, accountNumber: accountNumber.trim() || undefined });
      setOptions(data.data);
      setReason(''); setPlan(''); setStatus(''); setNewItemType(''); setNewSerial('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason) { toast.error('Select a reason'); return; }
    if (!newItemType) { toast.error('Select the new STB model'); return; }
    if (!newSerial.trim()) { toast.error('Enter the new STB serial'); return; }
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await gtplSaathiAPI.hardwareReplacement({
        stb: stb.trim() || undefined, accountNumber: accountNumber.trim() || undefined,
        reason, plan: plan || undefined, status: status || undefined, newItemType, newSerial: newSerial.trim(),
      });
      setResult({ ok: true, message: data.data?.message });
      toast.success('Submitted to GTPL Saathi');
    } catch (err) {
      const message = err.response?.data?.message || 'Hardware replacement failed';
      setResult({ ok: false, message });
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const select = (label, value, setValue, empty, list) => (
    <Field label={label}>
      <select className="form-control" value={value} onChange={(e) => setValue(e.target.value)}>
        <option value="">{empty}</option>
        {list.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  );

  return (
    <Screen narrow>
      <PageHead icon={Repeat} tone="adv" title="Replace a customer’s box"
        sub="Swap the old set-top box for another one directly on gtplsaathi.com." />

      <Notice tone="warn" icon={AlertTriangle}>
        This creates a real replacement record on GTPL's side and hasn't been confirmed against a live submission yet —
        double-check the first few directly on gtplsaathi.com.
      </Notice>

      <Panel icon={Search} tone="info" title="1. Find the old box">
        <form onSubmit={handleSearch} className="gx-form">
          <div className="gx-grid">
            <Field label="STB serial"><input className="form-control" value={stb} onChange={(e) => setStb(e.target.value)} placeholder="Current STB serial" /></Field>
            <Field label="Account number"><input className="form-control" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} placeholder="GTPL account number" /></Field>
          </div>
          <div className="gx-actions">
            <button type="submit" className="btn btn-primary" disabled={searching || !canReplace}>
              <Search size={15} />{searching ? 'Searching…' : 'Search'}
            </button>
            {!canReplace && <span className="gx-hint" style={{ color: 'var(--g-bad)', margin: 0 }}>You don't have permission for hardware replacement on GTPL Saathi.</span>}
          </div>
        </form>
      </Panel>

      {options && (
        <form onSubmit={handleSubmit}>
          <Panel icon={Tv} tone="adv" title="2. Replacement details"
            sub={<>Current box <b>{options.oldSerial}</b>{options.currentItem ? ` — ${options.currentItem}` : ''}</>}>
            <div className="gx-form">
              <div className="gx-grid">
                {select('Reason', reason, setReason, 'Select reason…', options.reasons)}
                {select('Plan (optional)', plan, setPlan, 'GTPL default', options.plans)}
                {select('Old box condition (optional)', status, setStatus, 'Not specified', options.statuses)}
              </div>
              <div className="gx-grid">
                {select('New box model', newItemType, setNewItemType, 'Select model…', options.newItems)}
                <Field label="New box serial">
                  <input className="form-control" value={newSerial} onChange={(e) => setNewSerial(e.target.value)} placeholder="New STB serial number" />
                </Field>
              </div>
            </div>
            <div className="gx-sticky">
              <button type="submit" className="btn btn-adv" disabled={submitting || !canReplace}>
                <Repeat size={15} />{submitting ? 'Submitting… (can take up to a minute)' : 'Submit replacement'}
              </button>
            </div>
          </Panel>

          {result && (
            <Notice tone={result.ok ? 'ok' : 'bad'} icon={result.ok ? CheckCircle : XCircle} title={result.ok ? 'Submitted' : 'Failed'}>
              {result.message}
            </Notice>
          )}
        </form>
      )}
    </Screen>
  );
}
